import { useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { Dialog } from '@factorialco/f0-react/dist/experimental'
import { Ai } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { periodFromParams } from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/periodFromParams'
import useChangeView from 'modules/shift_management/hooks/useChangeView'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { useLocationSelector } from 'modules/shift_management/hooks/useLocationSelector'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { DAY_IN_MS } from 'modules/shift_management/lib/consts/ShiftManagementFormats'
import { createLocationEntity } from 'modules/shift_management/lib/createLocationEntity'
import { buildFormattedRangeDate } from 'modules/shift_management/lib/dateFormatter'
import { buildDateRange } from 'modules/shift_management/lib/dateFormatter'
import { SchemaType } from 'modules/shift_management/lib/forms/GenerateCoverageForm'
import { useAllLocations } from 'modules/shift_management/lib/useSelectableLocations'
import View from 'modules/shift_management/store/internal/models/View'

interface SuccessDialogProps {
  showGenerateCoverageSuccessModal: boolean
  onClose: () => void
  form: UseFormReturn<SchemaType>
  setShowAutoScheduleModal: (show: boolean) => void
}

const SuccessDialog = ({
  showGenerateCoverageSuccessModal,
  onClose,
  form,
  setShowAutoScheduleModal,
}: SuccessDialogProps) => {
  const { locationIds, targetStartAt, daysToGenerate } = form.getValues()
  const { locations } = useAllLocations()
  const { access } = useCurrentSession()
  const { applyLocationSelection } = useLocationSelector()
  const locale = access.locale || 'en'
  const { onChangePeriod } = useChangeView()

  const [{ view }, dispatchSchedule] = useSchedule()
  const { isTimeFormat24H } = useIsTimeFormat24H()

  const formattedRangeDate = useMemo(() => {
    const endAt = buildDateRange(targetStartAt, daysToGenerate, DAY_IN_MS)
    return buildFormattedRangeDate(targetStartAt, endAt, locale)
  }, [targetStartAt, daysToGenerate, locale])

  const entityLocation = useMemo(() => {
    const locationId = locationIds?.[0]
    return locationId ? createLocationEntity(locationId, locations) : null
  }, [locationIds, locations])

  const onViewCoverage = () => {
    if (!entityLocation) return
    redirectUserAfterSubmit()
  }

  const onSchedule = () => {
    redirectUserAfterSubmit()
    setShowAutoScheduleModal(true)
  }

  const redirectUserAfterSubmit = () => {
    if (!entityLocation) return

    onClose()
    const newParams = {
      year: targetStartAt.getFullYear().toString(),
      month: (targetStartAt.getMonth() + 1).toString(),
      day: targetStartAt.getDate().toString(),
    }

    const dateFromParams = periodFromParams(newParams, view.viewType)

    const newView = new View({
      date: dateFromParams,
      viewType: view.viewType,
      groupType: view.groupType,
      isTimeFormat24H,
    })

    onChangePeriod(newView)
    applyLocationSelection([entityLocation])
    dispatchSchedule({ type: 'RESET_VIEW', view: newView })
  }

  return (
    <Dialog
      actions={{
        secondary: {
          label: i18n.t(
            'shift_management.coverage_section.generate_coverage_modal.success.view_coverage'
          ),
          onClick: () => onViewCoverage(),
        },
        primary: {
          icon: Ai,
          label: i18n.t(
            'shift_management.coverage_section.generate_coverage_modal.success.schedule'
          ),
          onClick: () => onSchedule(),
        },
      }}
      open={showGenerateCoverageSuccessModal}
      onClose={onClose}
      header={{
        title: i18n.t('shift_management.coverage_section.generate_coverage_modal.success.title'),
        description: i18n.t(
          'shift_management.coverage_section.generate_coverage_modal.success.description',
          {
            location: entityLocation?.name ?? '',
            date: formattedRangeDate,
          }
        ),
        type: 'positive',
      }}
    />
  )
}

export { SuccessDialog }
